import {
  Anchor,
  AtSign,
  Building2,
  FileText,
  PackageOpen,
  Ship,
  User,
  UserRound,
  Warehouse,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { api } from "@/services/api";
import { loadCarrierFilterOptions } from "@/lib/carrierFilterOptions";
import {
  classifyRateSource,
  RATE_SOURCE_TYPE_LABELS,
  type RateSourceType,
} from "@/types/rate";
import { formatContractRef } from "@/lib/formatContract";
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import { resolveDisplayName, roleLabel } from "@/lib/userDisplay";
import type { OrganizationMember } from "@/types/emailReports";
import { searchPortTags } from "./kaiPortTagSearch";
import {
  KAI_BOOKING_STATE_ICON,
  searchBookingStateTags,
} from "./kaiBookingStateTags";
import {
  CLIENT_ALLOWED_ENTITY_TAG_KINDS,
  isClientAllowedEntityTagKind,
} from "./kaiClientPortal";

/** The entity kinds a user can explicitly tag into a Kai turn with `@`. */
export type KaiEntityTagKind =
  | "user"
  | "sales_person"
  | "agent"
  | "consignee"
  | "shipper"
  | "carrier"
  | "port"
  | "contract_ref"
  | "booking_state";

/** Which lane side a `port` tag scopes to. */
export type KaiPortRole = "origin" | "destination" | "either";

/**
 * A structured entity reference the user attached to a single Kai message.
 * `value` is the canonical token Kai filters on (an app `user_id` for `user`,
 * a SCAC/code for `carrier`, or a canonical party code for
 * `sales_person`/`agent`/`consignee`/`shipper`).
 */
export interface KaiEntityTag {
  kind: KaiEntityTagKind;
  value: string;
  label: string;
  metadata?: KaiEntityTagMetadata;
}

/** Extra context carried by a `user` tag so Kai can scope to that person. */
export interface KaiEntityTagMetadata {
  role?: string;
  sales_person_link?: string | null;
  opened_by_values?: string[];
  /** Shipper registry contact, shown as the picker subtitle when present. */
  email?: string;
  phone?: string;
  /** `port` tag: which lane side it scopes to (origin / destination / either). */
  port_role?: KaiPortRole;
  /** `port` tag: human hint for a group pick (member ports) or country. */
  port_summary?: string;
  /** `contract_ref` tag: owning carrier, for the logo + rate-family match. */
  carrier?: string;
  /** `contract_ref` tag: prepaid / collect / spot / lcl family. */
  source_type?: RateSourceType;
  /** `contract_ref` tag: named account on the matched rate card, when present. */
  named_account?: string;
  /** `booking_state` tag: `state` (allow-list) or `state_not` (deny-list). */
  state_filter_mode?: "state" | "state_not";
  /** Plain-language ops hint for the tagged lifecycle stage. */
  ops_hint?: string;
  booking_state_id?: string;
}

export interface KaiEntityTagKindDef {
  kind: KaiEntityTagKind;
  label: string;
  hint: string;
  icon: LucideIcon;
  /** Bookings `filter-values` property, for the party / carrier kinds. */
  filterProperty?:
    | "sales_person"
    | "agent"
    | "consignee"
    | "shipper"
    | "carrier";
}

/** The picker menu shown after the user types `@`. */
export const KAI_ENTITY_TAG_KINDS: KaiEntityTagKindDef[] = [
  {
    kind: "user",
    label: "User",
    hint: "A Tideway teammate — scopes via their app account (sales link or Open By)",
    icon: User,
  },
  {
    kind: "sales_person",
    label: "Sales person",
    hint: "Sales attribution on bookings — works without a linked user account",
    icon: UserRound,
    filterProperty: "sales_person",
  },
  {
    kind: "agent",
    label: "Agent",
    hint: "Freight booking agent / forwarder",
    icon: Building2,
    filterProperty: "agent",
  },
  {
    kind: "consignee",
    label: "Consignee",
    hint: "End customer / importer",
    icon: PackageOpen,
    filterProperty: "consignee",
  },
  {
    kind: "shipper",
    label: "Shipper",
    hint: "Supplier / exporter — Kai can look up email and phone",
    icon: Warehouse,
    filterProperty: "shipper",
  },
  {
    kind: "carrier",
    label: "Carrier",
    hint: "Ocean line (MSC, Maersk, ZIM, …)",
    icon: Ship,
    filterProperty: "carrier",
  },
  {
    kind: "port",
    label: "Port",
    hint: "Origin / destination port or port group — scopes the lane",
    icon: Anchor,
  },
  {
    kind: "contract_ref",
    label: "Contract",
    hint: "Carrier / agent contract ref (prepaid, collect, or spot)",
    icon: FileText,
  },
  {
    kind: "booking_state",
    label: "Booking state",
    hint: "Where the shipment is in the ops lifecycle (Pending, Booked, Shipped, …)",
    icon: KAI_BOOKING_STATE_ICON,
  },
];

export const KAI_ENTITY_TAG_TRIGGER_ICON = AtSign;

/** `@` kinds shown in the picker for the caller's role. */
export function entityTagKindsForRole(
  role?: string | null,
): KaiEntityTagKindDef[] {
  if (role === "Client") {
    return KAI_ENTITY_TAG_KINDS.filter((def) =>
      isClientAllowedEntityTagKind(def.kind),
    );
  }
  return KAI_ENTITY_TAG_KINDS;
}

export function entityTagKindDef(kind: KaiEntityTagKind): KaiEntityTagKindDef {
  const def = KAI_ENTITY_TAG_KINDS.find((k) => k.kind === kind);
  // Every kind is in the registry; fall back defensively to the user kind.
  return def ?? KAI_ENTITY_TAG_KINDS[0];
}

/** Tailwind colour classes for an inline entity pill, keyed by kind. */
export function entityPillClassName(kind: KaiEntityTagKind): string {
  switch (kind) {
    case "user":
      return "bg-violet-500/15 text-violet-700 dark:text-violet-300";
    case "sales_person":
      return "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300";
    case "agent":
      return "bg-sky-500/15 text-sky-700 dark:text-sky-300";
    case "consignee":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
    case "shipper":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
    case "carrier":
      return "bg-teal-500/15 text-teal-700 dark:text-teal-300";
    case "port":
      return "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300";
    case "contract_ref":
      return "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300";
    case "booking_state":
      return "bg-orange-500/15 text-orange-700 dark:text-orange-300";
  }
}

/** Short badge label for a `port` tag role (POL / POD / Port). */
export function portRoleBadgeLabel(role: KaiPortRole | undefined): string {
  switch (role) {
    case "origin":
      return "POL";
    case "destination":
      return "POD";
    default:
      return "Port";
  }
}

/** The role picker options shown after choosing the Port kind. */
export const KAI_PORT_ROLE_OPTIONS: { role: KaiPortRole; label: string }[] = [
  { role: "either", label: "Either" },
  { role: "origin", label: "Origin (POL)" },
  { role: "destination", label: "Destination (POD)" },
];

// ── User lookup (org members + Focus "Open By" assignments) ─────────────────

interface CachedMember {
  member: OrganizationMember;
  openedByValues: string[];
}

let membersCache: Promise<CachedMember[]> | null = null;

async function loadMembers(): Promise<CachedMember[]> {
  if (!membersCache) {
    membersCache = (async () => {
      const [members, assignments] = await Promise.all([
        api.getOrganizationMembers(),
        api.getBookingAssignments().catch(() => []),
      ]);
      const openedByByUser = new Map<string, string[]>();
      for (const a of assignments) {
        const list = openedByByUser.get(a.user_id) ?? [];
        list.push(a.field_value);
        openedByByUser.set(a.user_id, list);
      }
      return members.map((member) => ({
        member,
        openedByValues: openedByByUser.get(member.user_id) ?? [],
      }));
    })().catch((err) => {
      // Reset so a transient failure can be retried on the next `@`.
      membersCache = null;
      throw err;
    });
  }
  return membersCache;
}

function memberToTag(entry: CachedMember): KaiEntityTag {
  const { member, openedByValues } = entry;
  return {
    kind: "user",
    value: member.user_id,
    label: resolveDisplayName(member.name, member.email),
    metadata: {
      role: member.role,
      sales_person_link: member.sales_person_link ?? null,
      opened_by_values: openedByValues,
    },
  };
}

function matchesMember(entry: CachedMember, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const name = entry.member.name?.toLowerCase() ?? "";
  const email = entry.member.email?.toLowerCase() ?? "";
  return name.includes(q) || email.includes(q);
}

// ── Search ──────────────────────────────────────────────────────────────────

/** Fetch tag candidates for a kind, filtered by the user's partial query. */
export async function searchEntityTags(
  kind: KaiEntityTagKind,
  query: string,
): Promise<KaiEntityTag[]> {
  if (kind === "user") {
    const members = await loadMembers();
    return members
      .filter((m) => matchesMember(m, query))
      .slice(0, 8)
      .map(memberToTag);
  }

  if (kind === "port") {
    return searchPortTags(query);
  }

  if (kind === "contract_ref") {
    return searchContractTags(query);
  }

  if (kind === "booking_state") {
    return searchBookingStateTags(query);
  }

  const def = entityTagKindDef(kind);
  const property = def.filterProperty ?? kind;
  const values = await api.getBookingFilterValues(property, query, 8);
  const fromBookings = values.map((v) => ({
    kind,
    value: v.value,
    label: v.label ?? formatDimensionValue(property, v.value),
  }));

  if (kind === "carrier") {
    const merged = await loadCarrierFilterOptions(query, fromBookings);
    return merged.slice(0, 8).map((v) => ({
      kind: "carrier" as const,
      value: v.value,
      label: v.label ?? v.value,
    }));
  }

  if (kind === "shipper") {
    const registry = await searchShipperRegistry(query, fromBookings);
    return dedupeTagsByValue([...fromBookings, ...registry]).slice(0, 8);
  }

  if (kind === "sales_person") {
    const registry = await searchSalesPersonRegistry(query, fromBookings);
    return dedupeTagsByValue([...fromBookings, ...registry]).slice(0, 8);
  }

  return fromBookings;
}

async function searchSalesPersonRegistry(
  query: string,
  existing: KaiEntityTag[],
): Promise<KaiEntityTag[]> {
  try {
    const res = await api.getSupplyChainEntities("sales_person", {
      search: query || undefined,
      per_page: 8,
    });
    const seen = new Set(existing.map((t) => t.value.toLowerCase()));
    return res.data
      .filter((e) => e.is_active !== false)
      .filter((e) => !seen.has(e.canonical_code.toLowerCase()))
      .map((e) => ({
        kind: "sales_person" as const,
        value: e.canonical_code,
        label: e.display_name || e.canonical_code,
      }));
  } catch {
    return [];
  }
}

async function searchShipperRegistry(
  query: string,
  existing: KaiEntityTag[],
): Promise<KaiEntityTag[]> {
  try {
    const res = await api.getSupplyChainEntities("shipper", {
      search: query || undefined,
      per_page: 8,
    });
    const seen = new Set(existing.map((t) => t.value.toLowerCase()));
    return res.data
      .filter((e) => e.is_active !== false)
      .filter((e) => !seen.has(e.canonical_code.toLowerCase()))
      .map((e) => {
        const email =
          typeof e.metadata?.primary_email === "string"
            ? e.metadata.primary_email.trim()
            : "";
        const phone =
          typeof e.metadata?.primary_phone === "string"
            ? e.metadata.primary_phone.trim()
            : "";
        return {
          kind: "shipper" as const,
          value: e.canonical_code,
          label: e.display_name || e.canonical_code,
          metadata:
            email || phone
              ? { email: email || undefined, phone: phone || undefined }
              : undefined,
        };
      });
  } catch {
    return [];
  }
}

/**
 * Search contract refs for the `@contract` tag. Candidate refs come from the
 * booking `contract_ref` filter values (ILIKE partial); each is then enriched
 * via the rate-cards API to recover its owning carrier and source family
 * (prepaid / collect / spot / lcl). When a ref maps to several families
 * (common: an agent prepaid number vs the carrier collect tariff) each
 * `(carrier, source_type)` becomes its own picker row, so the user tags the
 * exact contract they mean. The stored `value` is always the raw `contract_ref`.
 */
async function searchContractTags(query: string): Promise<KaiEntityTag[]> {
  const values = await api
    .getBookingFilterValues("contract_ref", query || undefined, 6)
    .catch(() => []);
  if (values.length === 0) return [];

  const perRef = await Promise.all(
    values.map((v) => enrichContractRef(v.value)),
  );
  return perRef.flat().slice(0, 8);
}

async function enrichContractRef(
  contractRef: string,
): Promise<KaiEntityTag[]> {
  const ref = contractRef.trim();
  if (!ref) return [];
  try {
    const res = await api.getRateCards({
      contract_ref: ref,
      status: "current",
      per_page: 5,
    });
    const families = new Map<string, KaiEntityTag>();
    for (const card of res.data) {
      const sourceType = classifyRateSource(card.source, card.is_spot);
      const key = `${card.carrier}:${sourceType}`.toLowerCase();
      if (families.has(key)) continue;
      families.set(key, {
        kind: "contract_ref",
        value: ref,
        label: formatContractRef(ref, card.named_account, card.carrier) || ref,
        metadata: {
          carrier: card.carrier,
          source_type: sourceType,
          named_account: card.named_account ?? undefined,
        },
      });
    }
    if (families.size > 0) return Array.from(families.values());
  } catch {
    // Fall through to the unenriched tag below.
  }
  // No rate card matched (booking-only ref) — tag the raw value.
  return [
    {
      kind: "contract_ref",
      value: ref,
      label: formatContractRef(ref) || ref,
    },
  ];
}

function dedupeTagsByValue(tags: KaiEntityTag[]): KaiEntityTag[] {
  const seen = new Set<string>();
  const out: KaiEntityTag[] = [];
  for (const tag of tags) {
    const key = `${tag.kind}:${tag.value.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}

/**
 * Search every entity kind at once and merge the matches. Used when the user
 * types a name straight after `@` (e.g. `@acme`) that isn't one of the kind
 * keywords, so we look it up as a party/person across all kinds.
 */
export async function searchAllEntityTags(
  query: string,
  role?: string | null,
): Promise<KaiEntityTag[]> {
  const kinds: KaiEntityTagKind[] =
    role === "Client"
      ? [...CLIENT_ALLOWED_ENTITY_TAG_KINDS]
      : [
          "carrier",
          "contract_ref",
          "agent",
          "consignee",
          "shipper",
          "sales_person",
          "port",
          "booking_state",
          "user",
        ];
  const perKind = await Promise.all(
    kinds.map((kind) =>
      searchEntityTags(kind, query)
        .then((results) => results.slice(0, 4))
        .catch(() => [] as KaiEntityTag[]),
    ),
  );
  return perKind.flat().slice(0, 12);
}

// ── Display / serialization helpers ─────────────────────────────────────────

/** Short subtitle for a tag chip, e.g. a user's role. */
export function entityTagSubtitle(tag: KaiEntityTag): string | undefined {
  if (tag.kind === "user") {
    return roleLabel(tag.metadata?.role) ?? undefined;
  }
  if (tag.kind === "shipper") {
    return tag.metadata?.email || tag.metadata?.phone || entityTagKindDef(tag.kind).label;
  }
  if (tag.kind === "port") {
    const role = portRoleBadgeLabel(tag.metadata?.port_role);
    return tag.metadata?.port_summary
      ? `${role} · ${tag.metadata.port_summary}`
      : role;
  }
  if (tag.kind === "contract_ref") {
    const parts = [
      tag.metadata?.carrier,
      tag.metadata?.source_type
        ? RATE_SOURCE_TYPE_LABELS[tag.metadata.source_type]
        : undefined,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : entityTagKindDef(tag.kind).label;
  }
  if (tag.kind === "booking_state") {
    return tag.metadata?.ops_hint ?? entityTagKindDef(tag.kind).label;
  }
  return entityTagKindDef(tag.kind).label;
}

/**
 * A short human-readable suffix appended to the stored message content so a
 * reloaded conversation still shows what was tagged, e.g.
 * `\n\n[Tagged: User Jane Doe · Agent Acme Logistics]`.
 */
export function formatTaggedContentSuffix(tags: KaiEntityTag[]): string {
  if (tags.length === 0) return "";
  const parts = tags.map((tag) => {
    const kindLabel = entityTagKindDef(tag.kind).label;
    return `${kindLabel} ${tag.label}`;
  });
  return `\n\n[Tagged: ${parts.join(" · ")}]`;
}

/** Regex matching the suffix produced by `formatTaggedContentSuffix`. */
const TAGGED_SUFFIX_RE = /\n\n\[Tagged:[^\]]*\]\s*$/;

/** Remove the tagged suffix from stored content for clean display. */
export function stripTaggedContentSuffix(content: string): string {
  return content.replace(TAGGED_SUFFIX_RE, "");
}
